import {
  Calendar,
  Scale,
  Mail,
  AlertCircle,
  FileText,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import Wrapper from "@/components/global/wrapper";
import Container from "@/components/global/container";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import EmailDisplay from "@/components/global/EmailDisplay";

const TermsPage = () => {
  const lastUpdated = "December 1, 2024";

  return (
    <Wrapper className="py-20 relative" data-oid="-mhh86w">
      <Container data-oid="bis_mbh">
        <div className="max-w-4xl mx-auto" data-oid="4lr1b2i">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-oid="w7ebr_v"
          >
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              data-oid="3ecwpez"
            >
              Terms of Service
            </h1>
            <div
              className="flex items-center justify-center gap-2 text-muted-foreground mb-4"
              data-oid="tcx6udu"
            >
              <Calendar className="w-4 h-4" data-oid="kuys:m-" />
              <span data-oid="._fhwo6">Last updated: {lastUpdated}</span>
            </div>
            <p
              className="text-xl text-muted-foreground max-w-2xl mx-auto"
              data-oid="yuy_-ko"
            >
              These terms govern your use of Blyrha's AI-powered marketing
              platform and services.
            </p>
          </motion.div>

          {/* Alert */}
          <motion.div
            className="p-4 rounded-lg border bg-amber-50 border-amber-200 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-oid="b.9hv1b"
          >
            <div className="flex items-start gap-3" data-oid="j08mi_-">
              <AlertCircle
                className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                data-oid="2ynv.mm"
              />
              <div data-oid=":680d0b">
                <h3
                  className="font-semibold text-amber-900 mb-1"
                  data-oid="x:2smw0"
                >
                  Agreement to Terms
                </h3>
                <p className="text-amber-800 text-sm" data-oid="ros1g__">
                  By accessing and using our platform, you accept and agree to
                  be bound by the terms and provision of this agreement. Please
                  read these terms carefully before using our services.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-oid=":ysgp:0"
          >
            <div className="space-y-8" data-oid="c5ljf9n">
              <section data-oid="1_o.4zy">
                <h2
                  className="text-2xl font-semibold mb-4 flex items-center gap-2"
                  data-oid="xo_rsmd"
                >
                  <FileText
                    className="w-6 h-6 text-blue-600"
                    data-oid="jphwnxx"
                  />
                  Service Description
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="ish.zke"
                >
                  <p data-oid="k3s04mx">
                    Blyrha provides AI-powered marketing automation tools and
                    services ("Service") to help businesses create, optimize,
                    and manage their marketing campaigns. Our platform includes
                    content generation, analytics, campaign management, and
                    integration capabilities.
                  </p>
                  <p data-oid="j6gg-5-">
                    We reserve the right to modify, suspend, or discontinue any
                    part of our Service at any time with or without notice. We
                    will not be liable to you or any third party for any such
                    modification, suspension, or discontinuance.
                  </p>
                </div>
              </section>

              <section data-oid="19nzdth">
                <h2
                  className="text-2xl font-semibold mb-4 flex items-center gap-2"
                  data-oid="pqsw5qj"
                >
                  <Users className="w-6 h-6 text-blue-600" data-oid=".hnq0-b" />
                  User Accounts and Responsibilities
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="dkpvtse"
                >
                  <h3
                    className="text-lg font-medium text-foreground"
                    data-oid="y3sqk_e"
                  >
                    Account Creation
                  </h3>
                  <p data-oid="cxwuuuj">
                    To access our Service, you must create an account and
                    provide accurate, complete information. You are responsible
                    for maintaining the confidentiality of your account
                    credentials.
                  </p>

                  <h3
                    className="text-lg font-medium text-foreground"
                    data-oid="fjii-1l"
                  >
                    Acceptable Use
                  </h3>
                  <p data-oid="ibypa4u">
                    You agree to use our Service only for lawful purposes and in
                    accordance with these Terms. You may not:
                  </p>
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="959i7dt"
                  >
                    <li data-oid="vdnyqri">
                      Use the Service for any illegal or unauthorized purpose
                    </li>
                    <li data-oid="_r48-f6">
                      Violate any applicable laws or regulations
                    </li>
                    <li data-oid="6c0ldln">
                      Interfere with or disrupt the Service or servers
                    </li>
                    <li data-oid="q_2bnk4">
                      Attempt to gain unauthorized access to any part of the
                      Service
                    </li>
                    <li data-oid=".7y34k9">
                      Upload or transmit malicious code or harmful content
                    </li>
                    <li data-oid="o__hs3t">
                      Violate the intellectual property rights of others
                    </li>
                    <li data-oid="bgkhajk">
                      Engage in any form of spam or unsolicited communications
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="h438vyu">
                <h2 className="text-2xl font-semibold mb-4" data-oid="5fjh6f8">
                  Intellectual Property Rights
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="tdm7pnm"
                >
                  <p data-oid="d7d1ig6">
                    The Service and its original content, features, and
                    functionality are and will remain the exclusive property of
                    Blyrha and its licensors. The Service is protected by
                    copyright, trademark, and other laws.
                  </p>
                  <p data-oid="h36x77:">
                    Content you create using our AI tools remains your property,
                    but you grant us a license to use, modify, and improve our
                    AI models based on usage patterns (in anonymized form).
                  </p>
                </div>
              </section>

              <section data-oid="3dmddle">
                <h2 className="text-2xl font-semibold mb-4" data-oid="8k.qgbi">
                  Payment Terms
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="dn-w-sr"
                >
                  <ul
                    className="list-disc list-inside space-y-2 ml-4"
                    data-oid="m_isst."
                  >
                    <li data-oid="k7p-8s5">
                      Subscription fees are billed in advance on a monthly or
                      yearly basis
                    </li>
                    <li data-oid="k4ddsva">
                      All fees are non-refundable except as required by law
                    </li>
                    <li data-oid="lxd-ezl">
                      We offer a 30-day money-back guarantee for new
                      subscriptions
                    </li>
                    <li data-oid="9bz4cf-">
                      Price changes will be communicated 30 days in advance
                    </li>
                    <li data-oid="._7w3te">
                      Failure to pay may result in service suspension or
                      termination
                    </li>
                    <li data-oid="g3-3g_0">
                      You're responsible for all taxes associated with your
                      subscription
                    </li>
                  </ul>
                </div>
              </section>

              <section data-oid="hp6xa0h">
                <h2 className="text-2xl font-semibold mb-4" data-oid="eq.m3g-">
                  Privacy and Data Protection
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="0vyu6g4"
                >
                  <p data-oid=":ol1f69">
                    Your privacy is important to us. Our collection and use of
                    personal information is governed by our Privacy Policy,
                    which is incorporated into these Terms by reference.
                  </p>
                  <p data-oid="0wck:sz">
                    We implement appropriate security measures to protect your
                    data and comply with applicable data protection laws
                    including GDPR and CCPA.
                  </p>
                </div>
              </section>

              <section data-oid="wds4zb5">
                <h2 className="text-2xl font-semibold mb-4" data-oid="7moczva">
                  Limitation of Liability
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="j-yphha"
                >
                  <p data-oid="9-beo67">
                    To the maximum extent permitted by law, Blyrha shall not be
                    liable for any indirect, incidental, special, consequential,
                    or punitive damages, including without limitation, loss of
                    profits, data, use, goodwill, or other intangible losses.
                  </p>
                  <p data-oid="x9wnkq.">
                    Our total liability for any claims arising from or relating
                    to this agreement shall not exceed the amount you paid us in
                    the twelve (12) months preceding the claim.
                  </p>
                </div>
              </section>

              <section data-oid="jbns698">
                <h2 className="text-2xl font-semibold mb-4" data-oid="farf4h7">
                  Termination
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="tqtcmdj"
                >
                  <p data-oid="bkj02.o">
                    You may terminate your account at any time by contacting us
                    or through your account settings. We may terminate or
                    suspend your account immediately, without prior notice, for
                    any reason including breach of these Terms.
                  </p>
                  <p data-oid="aw44e8f">
                    Upon termination, your right to use the Service will cease
                    immediately, and we may delete your account and data after a
                    reasonable period.
                  </p>
                </div>
              </section>

              <section data-oid="jw2fj9q">
                <h2 className="text-2xl font-semibold mb-4" data-oid="j87z9k-">
                  Governing Law
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="n35w38j"
                >
                  <p data-oid="7kx099r">
                    These Terms shall be governed by and construed in accordance
                    with the laws of the State of California, without regard to
                    its conflict of law principles.
                  </p>
                </div>
              </section>

              <section data-oid="9g136o2">
                <h2
                  className="text-2xl font-semibold mb-4 flex items-center gap-2"
                  data-oid="au8--ry"
                >
                  <Mail className="w-6 h-6 text-blue-600" data-oid="j5l8a1d" />
                  Contact Information
                </h2>
                <div
                  className="space-y-4 text-muted-foreground"
                  data-oid="hmdx:s:"
                >
                  <p data-oid="byqw6p3">
                    If you have any questions about these Terms of Service,
                    please contact us:
                  </p>
                  <div className="bg-muted p-4 rounded-lg" data-oid="nt52a76">
                    <p data-oid="pw0:xzs">
                      <strong className="text-foreground" data-oid="cpaelb9">
                        Email:
                      </strong>{" "}
                      <EmailDisplay emailId="legal" fallback="legal@blyrha.com" />
                    </p>
                    <p data-oid="f26luav">
                      <strong className="text-foreground" data-oid="q.ai9qt">
                        Address:
                      </strong>{" "}
                      2 Elias-Canetti-Strasse, 8050 Zürich, Switzerland
                    </p>
                    <p data-oid="akwrzb1">
                      <strong className="text-foreground" data-oid="7iy3uj0">
                        Phone:
                      </strong>{" "}
                      +41 441234567
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            className="relative mt-12 overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-slate-900 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            data-oid="e8m6qvs"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 opacity-80" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.5),_transparent_65%)] opacity-30" />
            <div className="relative px-6 py-10 md:px-12 text-center space-y-8">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
                Compliance ready
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
              </div>

              <div className="space-y-4">
                <h3 className="text-3xl md:text-4xl font-bold" data-oid="m16viti">
                  Ready to get started?
                </h3>
                <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto" data-oid="y4iox:r">
                  By continuing with Blyrha you acknowledge these terms and unlock enterprise-grade automations, audit trails, and dedicated legal support.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3 text-left text-sm text-white/80">
                {["GDPR & CCPA aligned","Role-based access policies","Export-ready audit logs"].map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white/5 rounded-2xl px-4 py-3">
                    <span className="h-2 w-2 rounded-full bg-emerald-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-oid="fsh6a1q">
                <Link to="/" data-oid="qh2w9zj">
                  <Button className="px-8 gap-2 shadow-lg shadow-blue-900/30" data-oid="o9:i1r2">
                    Start Using Blyrha
                  </Button>
                </Link>
                <Link to="/contact" data-oid="szr3u.m">
                  <Button
                    variant="secondary"
                    className="px-8 gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/30"
                    data-oid="jwxz9jk"
                  >
                    Ask Questions
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-white/70">
                Prefer a custom agreement? <Link to="/contact" className="underline underline-offset-4 text-white">Talk with legal</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </Container>
    </Wrapper>
  );
};

export default TermsPage;
